/**
 * 企業管理APIクライアント
 */

import { AuthenticatedApiClient } from '../client';
import type {
  Company,
  CompanyWithStats,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CompanyAccount,
  CompanyUser,
  CompanyEmailTemplates,
  UpdateEmailTemplatesRequest,
} from '@/lib/types/mise/company';

/**
 * 企業一覧を取得（認証付き）
 */
export async function getCompaniesAuthenticated(
  authHeaders: Record<string, string>,
  params?: {
    search?: string;
  },
): Promise<CompanyWithStats[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);

    const url = `/mise/companies${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const companies = await client.get<CompanyWithStats[]>(url);
    return companies;
  } catch (error) {
    console.error('Failed to fetch companies with auth:', error);
    throw error;
  }
}

/**
 * 企業詳細を取得（認証付き）
 */
export async function getCompanyByIdAuthenticated(
  companyId: string,
  authHeaders: Record<string, string>,
): Promise<CompanyWithStats | null> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const company = await client.get<CompanyWithStats>(`/mise/companies/${companyId}`);
    return company;
  } catch (error) {
    console.error(`Failed to fetch company with id ${companyId} (authenticated):`, error);
    throw error;
  }
}

/**
 * 企業を新規登録（認証付き）
 */
export async function createCompanyAuthenticated(
  request: CreateCompanyRequest,
  authHeaders: Record<string, string>,
): Promise<Company> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const company = await client.post<Company>('/mise/companies', request);
    return company;
  } catch (error) {
    console.error('Failed to create company:', error);
    throw error;
  }
}

/**
 * 企業情報を更新（認証付き）
 */
export async function updateCompanyAuthenticated(
  companyId: string,
  request: UpdateCompanyRequest,
  authHeaders: Record<string, string>,
): Promise<Company> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const company = await client.put<Company>(`/mise/companies/${companyId}`, request);
    return company;
  } catch (error) {
    console.error(`Failed to update company ${companyId}:`, error);
    throw error;
  }
}

/**
 * 企業を削除（認証付き）
 */
export async function deleteCompanyAuthenticated(
  companyId: string,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.delete(`/mise/companies/${companyId}`);
  } catch (error) {
    console.error(`Failed to delete company ${companyId}:`, error);
    throw error;
  }
}

/**
 * 企業を強制削除（認証付き、fkdev環境のみ）
 * 関連する全リソース（ユーザー、アカウント紐づけ、Cognitoユーザープール等）をカスケード削除
 */
export async function forceDeleteCompanyAuthenticated(
  companyId: string,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.delete(`/mise/companies/${companyId}?force=true`);
  } catch (error) {
    console.error(`Failed to force delete company ${companyId}:`, error);
    throw error;
  }
}

/**
 * 企業に紐づくアカウント一覧を取得（認証付き）
 */
export async function getCompanyAccountsAuthenticated(
  companyId: string,
  authHeaders: Record<string, string>,
): Promise<CompanyAccount[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const accounts = await client.get<CompanyAccount[]>(`/mise/companies/${companyId}/accounts`);
    return accounts;
  } catch (error) {
    console.error(`Failed to fetch company accounts for company ${companyId}:`, error);
    throw error;
  }
}

/**
 * 企業に紐づくユーザー一覧を取得（認証付き）
 */
export async function getCompanyUsersAuthenticated(
  companyId: string,
  authHeaders: Record<string, string>,
): Promise<CompanyUser[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const users = await client.get<CompanyUser[]>(`/mise/companies/${companyId}/users`);
    return users;
  } catch (error) {
    console.error(`Failed to fetch company users for company ${companyId}:`, error);
    throw error;
  }
}

/**
 * 未紐づけアカウント一覧を取得（認証付き）
 */
export async function getUnlinkedAccountsAuthenticated(
  authHeaders: Record<string, string>,
): Promise<CompanyAccount[]> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const accounts = await client.get<CompanyAccount[]>('/mise/companies/unlinked-accounts');
    return accounts;
  } catch (error) {
    console.error('Failed to fetch unlinked accounts:', error);
    throw error;
  }
}

/**
 * 企業のメールテンプレートを取得（認証付き）
 */
export async function getCompanyEmailTemplatesAuthenticated(
  companyId: string,
  authHeaders: Record<string, string>,
): Promise<CompanyEmailTemplates> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const templates = await client.get<CompanyEmailTemplates>(
      `/mise/companies/${companyId}/email-templates`,
    );
    return templates;
  } catch (error) {
    console.error(`Failed to fetch email templates for company ${companyId}:`, error);
    throw error;
  }
}

/**
 * 企業のメールテンプレートを更新（認証付き）
 */
export async function updateCompanyEmailTemplatesAuthenticated(
  companyId: string,
  request: UpdateEmailTemplatesRequest,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.put(`/mise/companies/${companyId}/email-templates`, request);
  } catch (error) {
    console.error(`Failed to update email templates for company ${companyId}:`, error);
    throw error;
  }
}

/**
 * アカウントのバッチ実行SQSメッセージを送信（認証付き）
 */
export async function executeBatchAuthenticated(
  accountId: string,
  forceAction: boolean,
  invokeBatchCache: boolean,
  invokeBatchLlm: boolean,
  forceDailySalesInitialize: boolean,
  invokeBatchStoreUsage: boolean,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.post(`/mise/accounts/${accountId}/execute-batch`, {
      force_action: forceAction,
      invoke_batch_cache: invokeBatchCache,
      invoke_batch_llm: invokeBatchLlm,
      force_daily_sales_initialize: forceDailySalesInitialize,
      invoke_batch_store_usage: invokeBatchStoreUsage,
    });
  } catch (error) {
    console.error(`Failed to execute batch for account ${accountId}:`, error);
    throw error;
  }
}

/**
 * 企業にアカウントを紐づける（認証付き）
 */
export async function linkAccountToCompanyAuthenticated(
  companyId: string,
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.post(`/mise/companies/${companyId}/accounts`, { account_id: accountId });
  } catch (error) {
    console.error(`Failed to link account ${accountId} to company ${companyId}:`, error);
    throw error;
  }
}
